import { parseISO } from "date-fns"

/**
 * Given an ISO date string, calculate the number of seconds, minutes, hours, and days since that date
 * with respect to the current time zone.
 * @param inputDate an ISO date string in GMT time zone
 * @returns an object with the number of seconds, minutes, hours, and days since the input date
 */
export function timeAgo(inputDate: string | Date): {
  seconds: number
  minutes: number
  hours: number
  days: number
} {
  // we have to append 'Z' to the date string to indicate it's in GMT
  // Then it is automatically converted to the local time zone.
  const date = typeof inputDate === "string" ? parseISO(inputDate + "Z") : inputDate

  // Calculate the difference in milliseconds
  const diffInMilliseconds: number = new Date().getTime() - date.getTime()

  const seconds: number = Math.floor(diffInMilliseconds / 1000)
  const minutes: number = Math.floor(seconds / 60)
  const hours: number = Math.floor(minutes / 60)
  const days: number = Math.floor(hours / 24)

  return { seconds, minutes, hours, days }
}

/**
 * Format a date as a string representing the time ago
 * @param date an ISO date string in GMT time zone
 * @returns a string representing the time ago
 */
export function formatTimeAgo(date: string): string {
  const { seconds, minutes, hours, days } = timeAgo(date)

  // Decide on the format based on the largest unit of time that is greater than zero
  if (days > 0) {
    return `${days} day${days > 1 ? "s" : ""} ago`
  } else if (hours > 0) {
    return `${hours} hour${hours > 1 ? "s" : ""} ago`
  } else if (minutes > 0) {
    return `${minutes} minute${minutes > 1 ? "s" : ""} ago`
  } else if (seconds > 0) {
    return `${seconds} second${seconds > 1 ? "s" : ""} ago`
  } else {
    return `Just now`
  }
}

// This is the function that generates a UTC timestamp, compatible with the server
// and consistent with the data that comes back from the API.
// Use this function INSTEAD of new Date().toISOString() to generate timestamps.
export function nowInUTC(): string {
  const timestamp = Date.now()
  const date = new Date(timestamp)

  // Format the date part (YYYY-MM-DD)
  const year = date.getUTCFullYear()
  const month = (date.getUTCMonth() + 1).toString().padStart(2, "0") // Months are zero-based
  const day = date.getUTCDate().toString().padStart(2, "0")

  // Format the time part (HH:mm:ss)
  const hours = date.getUTCHours().toString().padStart(2, "0")
  const minutes = date.getUTCMinutes().toString().padStart(2, "0")
  const seconds = date.getUTCSeconds().toString().padStart(2, "0")

  // Get milliseconds and append random microseconds (0-999) to create a microsecond precision.
  const milliseconds = date.getUTCMilliseconds().toString().padStart(3, "0")
  const microseconds = Math.floor(Math.random() * 1000)
    .toString()
    .padStart(3, "0")

  // Combine everything together
  const formattedTime = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}${microseconds}`
  return formattedTime
}

export function getTimezoneNameIANA(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}

/**
 * Format seconds into a timestamp string (MM:SS or HH:MM:SS for videos > 1 hour)
 * @param seconds - The number of seconds to format
 * @returns A formatted timestamp string like "02:34" or "01:23:45"
 */
export function formatSecondsToTimestamp(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) {
    return "00:00"
  }

  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = Math.floor(seconds % 60)

  if (hours > 0) {
    return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`
  }
  return `${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`
}

/**
 * Parse a timestamp value into seconds.
 *
 * Accepted formats:
 *   - `number`       — already a numeric count of seconds (e.g. backend sends a JS number)
 *   - `"HH:MM:SS"`   — colon-delimited hours, minutes, seconds
 *   - `"MM:SS"`      — colon-delimited minutes and seconds
 *   - `"M"` / `"MM"` — bare integer with no colons, treated as **minutes**
 *                       (the backend sometimes omits the `:SS` component and
 *                       sends only the minute portion as a plain integer string)
 *
 * Returns 0 for any unparseable input.
 */
export function parseTimestampToSeconds(timestamp: string | number): number {
  if (timestamp === null || timestamp === undefined || timestamp === "") return 0

  // Numeric type → treat as minutes (same as a bare integer string with no colons)
  if (typeof timestamp === "number") {
    return Number.isFinite(timestamp) && timestamp >= 0 ? timestamp * 60 : 0
  }

  const parts = timestamp.split(":").map(Number)
  if (parts.some(isNaN)) return 0

  if (parts.length === 3) {
    // HH:MM:SS
    return parts[0] * 3600 + parts[1] * 60 + parts[2]
  } else if (parts.length === 2) {
    // MM:SS
    return parts[0] * 60 + parts[1]
  } else if (parts.length === 1) {
    // Bare integer with no colon delimiters — backend sends minutes only
    return parts[0] * 60
  }

  return 0
}
